import { Link, Paragraph } from "@eworkit/iris";
import { Formik, FormikHelpers, FormikProps } from "formik";
import React, { useCallback, useContext, useState } from "react";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { Trans, useTranslation } from "react-i18next";
import { useHistory, useLocation } from "react-router-dom";

import { useAnalytics } from "@/components/analytics/useAnalytics";
import { VALIDATION_TYPES } from "@/components/Formik";
import { Text } from "@/components/Text";
import { errorEntryType, hasCode } from "@/core/api";
import { getOAuthURL, ICredentials, login, refresh, TOAuthActionType, TOAuthServiceType } from "@/data/session";
import { MessagesContext } from "@/eworkitComponents";
import { Flex } from "@/eworkitComponents";
import { useRecaptcha } from "@/infrastructure/Recaptcha";
import { ACTION_NAMES } from "@/infrastructure/Recaptcha/types";
import { RouteButton } from "@/infrastructure/Route";
import { router } from "@/views/router";
import { GoogleButton } from "@/views/Session/components/GoogleButton";
import { LinkedInButton } from "@/views/Session/components/LinkedInButton";
import { useCreateSession } from "@/views/Session/hooks";

import { EmailRequired, FORM_MESSENGER_CONTEXT, LoginForm, validationSchema } from "./components";

import "./session-login.scss";

interface Props {
  linkProfile?: () => Promise<boolean>;
  oAuthRedirectUriQueryString?: string;
  jobRequestId?: number;
}

export const SessionLogin: React.FunctionComponent<Props> = React.memo(
  ({ linkProfile, oAuthRedirectUriQueryString, jobRequestId }) => {
    const { t } = useTranslation("public");
    const { executeRecaptcha } = useGoogleReCaptcha();

    const { addMessage } = useContext(MessagesContext);
    const { sendEvent } = useAnalytics();

    const [email, setEmail] = useState("");

    const location = useLocation<any>();
    const { createSession } = useCreateSession();
    const history = useHistory();

    const loginWithHandler = useCallback(
      (serviceType: TOAuthServiceType, actionType: TOAuthActionType) => () => {
        window.location.href = getOAuthURL(
          serviceType,
          actionType,
          window.location.origin,
          oAuthRedirectUriQueryString,
        );
      },
      [oAuthRedirectUriQueryString],
    );

    const handleSubmit = async (values: ICredentials, actions: FormikHelpers<ICredentials>) => {
      let linkingResult = true;

      try {
        const recaptchaToken = useRecaptcha && executeRecaptcha ? await executeRecaptcha(ACTION_NAMES.login) : "";
        let sessionUser = await login({ recaptchaToken }, values);

        sendEvent({ type: "login", method: "email" });

        if (linkProfile) {
          linkingResult = await linkProfile();
          sessionUser = await refresh();
        }

        if (linkingResult) {
          await createSession(sessionUser);
        }

        if (jobRequestId) {
          history.push(router.get("job-requests.detail", { jobRequestId }).path);
        }
      } catch (response) {
        const payload = errorEntryType(response.payload);
        const notConfirmedError = hasCode(payload, VALIDATION_TYPES.EMAIL_NOT_CONFIRMED);

        if (notConfirmedError) {
          setEmail(values.username);
        }

        addMessage({
          text: t("SESSION_LOGIN.INCORRECT_PASS"),
          type: "warning",
          context: FORM_MESSENGER_CONTEXT,
        });
        actions.setSubmitting(false);
      }
    };

    const renderForm = (formikProps: FormikProps<ICredentials>) => <LoginForm form={formikProps} />;

    const renderLoginForm = () => {
      return (
        <Flex direction="column" grow={1}>
          <Flex innerMargin="lg" direction="column" padding={[false, false, "md", false]}>
            <Formik
              initialValues={{ username: location.state && location.state.email, password: "" }}
              validationSchema={validationSchema}
              onSubmit={handleSubmit}>
              {props => renderForm(props)}
            </Formik>
          </Flex>

          <Flex direction="row" align="center" margin={["md", false, false]}>
            <Flex grow={1} direction="column" innerBorder>
              <span />
              <span />
            </Flex>
            <Flex padding={[false, "md"]}>
              <Text color="grey1">{t("SESSION_LOGIN.BUTTON_SEPARATOR")}</Text>
            </Flex>
            <Flex grow={1} direction="column" innerBorder>
              <span />
              <span />
            </Flex>
          </Flex>

          <Flex direction="row" innerMargin="md" padding={["lg", false, false, false]}>
            <LinkedInButton onClick={loginWithHandler("LinkedIn", "login")} display="block">
              <Text color="n700" size="lg">
                {t("SESSION_LOGIN.BUTTON_LOGIN_LINKEDIN")}
              </Text>
            </LinkedInButton>

            <GoogleButton onClick={loginWithHandler("Google", "login")}>
              <Text color="n700" size="lg">
                {t("SESSION_LOGIN.BUTTON_LOGIN_GOOGLE")}
              </Text>
            </GoogleButton>
          </Flex>

          <Flex direction="row" padding={["xl", false]} justify="center">
            <Text size="md" align="center" color="black">
              {t("SESSION_REGISTRATION.SWITCH.TEXT")}
              <RouteButton
                variant="link"
                display="inline-block"
                path={router.get("register-form").path}
                className="session-login">
                <Text size="md">{t("SESSION_REGISTRATION.SWITCH.LINK")}</Text>
              </RouteButton>
            </Text>
          </Flex>

          <Flex direction="row" justify="center">
            <Paragraph size="p-xs" align="center" color="n500">
              <Trans ns="public" i18nKey="GOOGLE_RECAPTCHA_INFORMATION">
                <Link href="https://policies.google.com/privacy" target="_blank" />
                <Link href="https://policies.google.com/terms" target="_blank" />
              </Trans>
            </Paragraph>
          </Flex>
        </Flex>
      );
    };

    const renderNotConfirmed = () => {
      return <EmailRequired email={email} />;
    };

    return (
      <>
        {!email && renderLoginForm()}
        {email && renderNotConfirmed()}
      </>
    );
  },
);
